import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { logger } from 'src/analytics/KatalLogger';
import { LoadingSpinner } from 'src/components/common/LoadingSpinner';
import {
  fetchAllActiveUserAccessWithCorpSegments,
  fetchCurrentUserAuthorization,
  fetchUserAccessAndAuthorization
} from 'src/features/business-group/access-authorization/redux/userAuthorizationSlice';
import { fetchBusinessGroups } from 'src/features/business-group/businessGroupSlice';
import { fetchCriticalMasterSegments, fetchDeferredCorpSegments } from 'src/store/slices/corpSegmentSlice';
import { fetchCCAndPLDropdownValuesOfAllActiveBusinessGroups } from 'src/store/thunks/corpSegmentThunks';
import { performance } from 'src/utils/performance-utils';
import { AppDispatch } from '../store/store';
import { useFlashMessage } from './AppBannerContext';
import { fetchXptMetadata } from './AppMetadataSlice';
import { useAuth } from './auth/AuthContextProvider';
import { fetchForecastPlanningCycles, fetchPlanningCycles, fetchScenarios, fetchSnapshots } from 'src/features/planning-cycle/redux';

interface AppInitializerProps {
  children: React.ReactNode;
}

/**
 * AppInitializer component handles the initialization of the application
 * by loading all necessary data in a specific order to ensure dependencies
 * are properly resolved.
 *
 * The initialization process is divided into stages:
 * 1. Parallel Init - Metadata, User Access, Business Groups, Critical Segments (CC+PL only), Planning Cycles
 * 2. Dropdown Values - Cost center and product line dropdown values (needs CC+PL from Stage 1)
 * 3. User Authorization - Current user's specific authorizations (needs dropdowns from Stage 2)
 * 4. (Background) Deferred Segments - Large files like project.json (17MB), location.json (4.8MB)
 *    loaded after homepage renders. BG wrapper awaits these before rendering BG routes.
 */
const AppInitializer: React.FC<AppInitializerProps> = ({ children }) => {
  const { userAlias, isDev, isInitiallyDev } = useAuth();
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();
  const { addMessage } = useFlashMessage();

  useEffect(() => {
    const initializeApp = async () => {
      // Tracks whether the user-access query (listUserAccess) itself SUCCEEDED. Only when
      // it did can an init failure be attributed to the caller genuinely having no access
      // If the access query itself failed,the failure is a real service issue and must
      // surface the error banner.
      let userAccessSucceeded = false;
      try {
        performance.mark('app-initialization-start');

        // -------------------------------------------------------------------------
        // Parallel batch: Metadata, User Access, Business Groups, Critical Segments, Planning Cycles
        //
        // Critical Segments only downloads Cost Center + Product Line S3 files.
        // Project (17MB), Location (4.8MB), Account, Channel, Company are deferred
        // to after the homepage renders — they're not needed for homepage or auth.
        // -------------------------------------------------------------------------
        performance.mark('parallel-init-start');
        performance.mark('planning-cycles-start');
        logger.info('Initialization: Loading metadata, user access, business groups, critical segments, and planning cycles in parallel');

        const planningCyclesPromise = Promise.all([
          dispatch(fetchScenarios()),
          dispatch(fetchSnapshots()),
          dispatch(fetchPlanningCycles()),
          dispatch(fetchForecastPlanningCycles())
        ]).then((results) => {
          performance.mark('planning-cycles-end');
          performance.measure('Planning Cycles Loading Time', 'planning-cycles-start', 'planning-cycles-end');
          logger.debug(`Planning cycles loaded in ${performance.getEntriesByName('Planning Cycles Loading Time')[0].duration.toFixed(2)}ms`);
          return results;
        });

        const [metadataResult, userAccessResult, businessGroupsResult, segmentsResult] = await Promise.all([
          dispatch(fetchXptMetadata()),
          dispatch(fetchUserAccessAndAuthorization({ userAlias, isInitiallyDev })),
          dispatch(fetchBusinessGroups()),
          dispatch(fetchCriticalMasterSegments())
        ]);

        userAccessSucceeded = userAccessResult.meta.requestStatus === 'fulfilled';

        if (metadataResult.meta.requestStatus === 'rejected') {
          throw new Error('Failed to load application metadata');
        }
        if (userAccessResult.meta.requestStatus === 'rejected') {
          throw new Error('Failed to load user access and authorization data');
        }
        if (businessGroupsResult.meta.requestStatus === 'rejected') {
          throw new Error('Failed to load business groups data');
        }
        if (segmentsResult.meta.requestStatus === 'rejected') {
          throw new Error('Failed to load master segments data');
        }

        performance.mark('parallel-init-end');
        performance.measure('Parallel Init Loading Time', 'parallel-init-start', 'parallel-init-end');
        logger.debug(
          `Parallel init completed in ${performance.getEntriesByName('Parallel Init Loading Time')[0].duration.toFixed(2)}ms`
        );

        // -------------------------------------------------------------------------
        // Stage 2: Fetch cost center and product line dropdown values
        // Only needs CC + PL master segments (already loaded in critical phase)
        // -------------------------------------------------------------------------
        performance.mark('dropdowns-start');
        logger.info('Initialization Stage 2: Loading dropdown values');
        const dropdownsResult = await dispatch(fetchCCAndPLDropdownValuesOfAllActiveBusinessGroups());

        if (dropdownsResult.meta.requestStatus === 'rejected') {
          throw new Error('Failed to load cost center and product line dropdown values');
        }

        performance.mark('dropdowns-end');
        performance.measure('Dropdowns Loading Time', 'dropdowns-start', 'dropdowns-end');
        logger.debug(`Dropdown values loaded in ${performance.getEntriesByName('Dropdowns Loading Time')[0].duration.toFixed(2)}ms`);

        // -------------------------------------------------------------------------
        // Stage 3: Fetch current user authorization and all user access with corp segments
        // Only needs CC+PL dropdown values — no dependency on deferred segments
        // -------------------------------------------------------------------------
        performance.mark('authorization-start');
        logger.info('Initialization Stage 3: Loading current user authorization and user access with corp segments');

        const [currentUserAuthResult, allUserAccessResult] = await Promise.all([
          dispatch(fetchCurrentUserAuthorization({ userAlias, isDev })),
          dispatch(fetchAllActiveUserAccessWithCorpSegments())
        ]);

        if (currentUserAuthResult.meta.requestStatus === 'rejected') {
          throw new Error('Failed to load current user authorization data');
        }
        if (allUserAccessResult.meta.requestStatus === 'rejected') {
          throw new Error('Failed to load user access with corp segments data');
        }

        performance.mark('authorization-end');
        performance.measure('Authorization Loading Time', 'authorization-start', 'authorization-end');
        logger.debug(`User authorization loaded in ${performance.getEntriesByName('Authorization Loading Time')[0].duration.toFixed(2)}ms`);

        // -------------------------------------------------------------------------
        // Await planning cycles (started in parallel)
        // -------------------------------------------------------------------------
        const planningCycleResults = await planningCyclesPromise;

        const failedPlanningCycleRequest = planningCycleResults.find((result) => result.meta.requestStatus === 'rejected');
        if (failedPlanningCycleRequest) {
          throw new Error('Failed to load planning cycle data');
        }

        // -------------------------------------------------------------------------
        // Complete initialization and log performance metrics
        // -------------------------------------------------------------------------
        performance.mark('app-initialization-end');
        performance.measure('Total Initialization Time', 'app-initialization-start', 'app-initialization-end');

        const totalTime = performance.getEntriesByName('Total Initialization Time')[0];
        logger.info(`Application initialization completed successfully in ${totalTime.duration.toFixed(2)}ms`);

        const stageTimings = [
          { name: 'Parallel Init (Metadata+Access+CriticalSegments)', ms: Math.round(performance.getEntriesByName('Parallel Init Loading Time')[0].duration) },
          { name: 'Dropdowns (CC+PL)', ms: Math.round(performance.getEntriesByName('Dropdowns Loading Time')[0].duration) },
          { name: 'Authorization', ms: Math.round(performance.getEntriesByName('Authorization Loading Time')[0].duration) },
          { name: 'Planning Cycles', ms: Math.round(performance.getEntriesByName('Planning Cycles Loading Time')[0].duration) }
        ];

        logger.info('App initialization stage timings', {
          logType: 'xpt-init-stages',
          totalDurationMs: Math.round(totalTime.duration),
          parallelInitMs: stageTimings[0].ms,
          dropdownsMs: stageTimings[1].ms,
          authorizationMs: stageTimings[2].ms,
          planningCyclesMs: stageTimings[3].ms
        });
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        logger.error('Failed to initialize the application:', {
          error: errorMessage,
          stack: error instanceof Error ? error.stack : undefined
        });

        // For a no-access user, we want to render the AccessRestricted / request-access page.
        // Prioritize that over the generic error banner.
        let isNoAccessUser = false;
        if (userAccessSucceeded) {
          const authResult = await dispatch(fetchCurrentUserAuthorization({ userAlias, isDev }));
          isNoAccessUser = authResult.meta.requestStatus === 'rejected';
        }

        if (!isNoAccessUser) {
          addMessage({
            type: 'error',
            content:
              'We encountered an issue while initializing key application services. As a result, some features may not be available. Try refreshing the page or contact support if the issue persists.'
          });
        }
      } finally {
        // Always fire deferred segment downloads — even if later init stages failed.
        // The BG wrapper waits on deferredCorpSegmentsStatus; if we never dispatch this,
        // the BG wrapper hangs on Loading forever.
        // fetchDeferredCorpSegments internally checks if there are segments to load (idempotent).
        dispatch(fetchDeferredCorpSegments());

        setIsInitialized(true);

        try {
          performance.mark('xpt-app-ready');
          performance.measure('xpt-total-rendered', 'xpt-page-load-start', 'xpt-app-ready');
          const duration = performance.getEntriesByName('xpt-total-rendered')[0].duration;
          logger.info('xPT App Rendered', {
            logType: 'xpt-total-rendered',
            pageLoadDurationMs: Math.round(duration)
          });
        } catch {
          // xpt-page-load-start mark may not exist in test environments
        }
      }
    };

    initializeApp();
  }, [dispatch, userAlias, isDev, isInitiallyDev, addMessage]);

  if (!isInitialized) {
    return <LoadingSpinner />;
  }

  return <>{children}</>;
};

export default AppInitializer;
